import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { CalendarClock, Mic, FileCheck2, Building2, Briefcase, GraduationCap, FileText, FileSpreadsheet, Captions, FileType2, Lock, Presentation } from "lucide-react";
import { DemoBadge } from "@/components/Brand";
import { TranscriptList } from "@/components/meeting/Transcript";
import { useApp } from "@/lib/i18n";
import { getDemo } from "@/components/landing/demoData";

const Eyebrow = ({ children }) => <p className="text-xs font-bold uppercase tracking-wider text-violet-700">{children}</p>;
const H2 = ({ children }) => <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">{children}</h2>;

export const Flow = () => {
  const { L } = useApp();
  const steps = [
    [CalendarClock, L("Sebelum", "Before"), L("Tetapkan tajuk, jenis mesyuarat, peserta, bahasa transkripsi dan bahasa hasil. Pilih template minit rasmi, projek, pasukan, pelanggan atau kuliah.", "Set title, meeting type, participants, transcription and output language. Choose official, project, team, client or lecture templates.")],
    [Mic, L("Semasa", "During"), L("Rakam terus dari pelayar dengan pemasa, jeda dan sambung — atau muat naik audio/video. Rakaman disimpan dahulu sebelum diproses.", "Record in the browser with timer, pause and resume — or upload audio/video. Audio is saved before processing.")],
    [FileCheck2, L("Selepas", "After"), L("Dapatkan transkrip bertanda masa, minit draf, keputusan, tindakan susulan, nota visual dan chat AI. Semak, edit, eksport atau kongsi.", "Get timestamped transcripts, draft minutes, decisions, actions, visual notes and AI chat. Review, edit, export or share.")],
  ];
  return (
    <section id="cara" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <Eyebrow>{L("Cara Berfungsi", "How it works")}</Eyebrow>
      <H2>{L("Sebelum, semasa dan selepas mesyuarat.", "Before, during and after the meeting.")}</H2>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {steps.map(([Icon, t, d], i) => (
          <div key={t} className="relative rounded-2xl border border-slate-200 bg-white p-7 transition-shadow hover:shadow-md" data-testid={`flow-step-${i}`}>
            <span className="absolute right-6 top-6 font-heading text-5xl font-extrabold text-slate-100">0{i + 1}</span>
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-violet-100 text-violet-700"><Icon size={20} /></div>
            <h3 className="mt-5 text-xl font-semibold">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const ExportsPreview = () => {
  const { L } = useApp();
  const files = [[Presentation, "PPTX", L("Pembentangan daripada dokumen", "Presentation from documents")], [FileText, "PDF", L("Minit rasmi, nota kuliah, laporan & temubual", "Official minutes, lecture notes, reports & interviews")], [FileType2, "DOCX", L("Minit boleh diedit", "Editable minutes")], [Captions, "TXT / SRT", L("Transkrip & sari kata", "Transcript & subtitles")], [FileSpreadsheet, "CSV", L("Tindakan susulan", "Action items")]];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {files.map(([Icon, f, d]) => <div key={f} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4"><Icon className="text-violet-600" /><div><p className="font-bold">{f}</p><p className="text-xs text-slate-500">{d}</p></div></div>)}
      {[].map((f) => <div key={f} className="flex items-center gap-3 rounded-xl border border-dashed border-slate-200 p-4 text-slate-400"><Lock size={18} /><div><p className="font-bold">{f}</p><p className="text-xs">{L("Fasa berikutnya", "Next phase")}</p></div></div>)}
    </div>
  );
};

export const Previews = () => {
  const { L, lang } = useApp();
  const { segments: DEMO_SEGMENTS, names: DEMO_NAMES, minutes: DEMO_MINUTES } = getDemo(lang);
  const tabs = [
    ["transcript", L("Transkrip", "Transcript"), <TranscriptList segments={DEMO_SEGMENTS} names={DEMO_NAMES} maxH="max-h-[420px]" />],
    ["minutes", L("Minit", "Minutes"), <div className="prose-minit"><ReactMarkdown>{DEMO_MINUTES}</ReactMarkdown></div>],
    ["exports", L("Fail Eksport", "Export Files"), <ExportsPreview />],
  ];
  const [tab, setTab] = useState("transcript");
  return (
    <section id="demo" className="bg-slate-900 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-wider text-violet-300">{L("Lihat hasilnya", "See the output")}</p>
        <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">{L("Satu rakaman. Semua dokumen mesyuarat.", "One recording. Every meeting document.")}</h2>
        <div className="mt-8 flex flex-wrap gap-2" role="tablist">
          {tabs.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`preview-tab-${k}`}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === k ? "bg-white text-slate-900" : "border border-slate-700 text-slate-300 hover:border-slate-500"}`}>{l}</button>
          ))}
        </div>
        <div className="fade-up mt-6 rounded-2xl bg-white p-5 sm:p-8" key={tab} data-testid="preview-panel">
          <div className="mb-4 flex items-center justify-between"><span className="text-sm font-semibold text-slate-500">{L("Komponen aplikasi sebenar · data contoh", "Real app components · sample data")}</span><DemoBadge /></div>
          {tabs.find(([k]) => k === tab)[2]}
        </div>
      </div>
    </section>
  );
};

export const UseCases = () => {
  const { L } = useApp();
  const items = [
    [Building2, L("Organisasi & Pejabat", "Organisations & Offices"), L("Minit rasmi format Malaysia: bilangan mesyuarat, kehadiran, perkara berbangkit, keputusan dan tindakan.", "Official Malaysian-format minutes: meeting number, attendance, matters arising, decisions and actions.")],
    [Briefcase, L("SME & Perniagaan", "SMEs & Business"), L("Perbincangan pelanggan dan mesyuarat projek jadi senarai tugasan yang jelas dengan pemilik dan tarikh akhir.", "Client discussions and project meetings become clear task lists with owners and deadlines.")],
    [GraduationCap, L("Pendidikan", "Education"), L("Nota kuliah tersusun untuk pensyarah dan pelajar — konsep utama, istilah dan soalan ulang kaji.", "Structured lecture notes for lecturers and students — key concepts, terms and revision questions.")],
  ];
  return (
    <section id="fungsi" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8">
      <Eyebrow>{L("Fungsi & kegunaan", "Features & use cases")}</Eyebrow>
      <H2>{L("Direka untuk cara mesyuarat di Malaysia.", "Built for how Malaysia meets.")}</H2>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {items.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl border border-slate-200 bg-white p-7 transition-transform hover:-translate-y-1" data-testid="use-case-card">
            <Icon className="text-violet-600" />
            <h3 className="mt-4 text-lg font-semibold">{t}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-6 grid gap-3 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
        {[L("Transkrip bertanda masa", "Timestamped transcript"), L("Label & nama pembicara", "Speaker labels & names"), L("Minit Rasmi, Nota Kuliah, Laporan, Temubual", "Official Minutes, Lecture Notes, Reports, Interviews"), L("Kongsi ke WhatsApp & e-mel", "Share to WhatsApp & email"), L("Tindakan: senarai & Kanban", "Actions: list & Kanban"), L("Folder, tag & kegemaran", "Folders, tags & favourites"), L("Pautan kongsi dengan tarikh luput", "Share links with expiry"), L("Workspace & peranan pasukan", "Team workspaces & roles")].map((f) => (
          <div key={f} className="rounded-xl bg-slate-100 px-4 py-3 font-medium">{f}</div>
        ))}
      </div>
    </section>
  );
};
